/**
 * ARIA Booking/Scheduling Page
 * Version: 1.0.0
 */

import { useState, useMemo } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  User,
  Plus,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { Job, Technician } from '../types';
import { ServiceOrdersModal } from '../components/ServiceOrdersModal';

const BOOKING_DATA_QUERY = gql`
  query BookingData($startDate: String!, $endDate: String!) {
    jobsInDateRange(startDate: $startDate, endDate: $endDate) {
      id
      sytelineOrderNumber
      seNumber
      customerName
      fullAddress
      city
      scheduling {
        scheduledDate
        startTime
        endTime
        appointmentDescription
      }
      technician {
        id
        fullName
      }
      status
    }
    technicians {
      items {
        id
        fullName
        employmentStatus
      }
    }
  }
`;

const SCHEDULE_JOB_MUTATION = gql`
  mutation ScheduleJob($id: ID!, $technicianId: ID!, $scheduledDate: String!, $startTime: String) {
    scheduleJob(id: $id, technicianId: $technicianId, scheduledDate: $scheduledDate, startTime: $startTime) {
      id
      scheduling {
        scheduledDate
        startTime
      }
      technician {
        id
        fullName
      }
    }
  }
`;

type ViewMode = 'week' | 'day';

export default function Booking() {
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedTechnician, setSelectedTechnician] = useState<string>('all');
  const [isAddJobModalOpen, setIsAddJobModalOpen] = useState(false);
  const [selectedDateForNewJob, setSelectedDateForNewJob] = useState<Date | undefined>(undefined);

  // Calculate date range based on view mode
  const { startDate, endDate, dateRange } = useMemo(() => {
    const start = new Date(currentDate);
    const end = new Date(currentDate);
    const range: Date[] = [];

    if (viewMode === 'week') {
      // Start from Sunday
      start.setDate(start.getDate() - start.getDay());
      end.setDate(start.getDate() + 6);
      for (let i = 0; i < 7; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        range.push(d);
      }
    } else {
      range.push(new Date(currentDate));
    }

    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
      dateRange: range,
    };
  }, [currentDate, viewMode]);

  const { data, loading, refetch } = useQuery(BOOKING_DATA_QUERY, {
    variables: { startDate, endDate },
  });

  const [_scheduleJob] = useMutation(SCHEDULE_JOB_MUTATION, {
    onCompleted: () => refetch(),
  });
  void _scheduleJob; // Will be used when drag-drop scheduling is implemented

  const jobs = (data?.jobsInDateRange || []) as Job[];
  const technicians = (data?.technicians?.items || []) as Technician[];

  // Filter jobs by technician if selected
  const filteredJobs =
    selectedTechnician === 'all'
      ? jobs
      : jobs.filter((job) => job.technician?.id === selectedTechnician);

  // Group jobs by date
  const jobsByDate = filteredJobs.reduce(
    (acc, job) => {
      const date = job.scheduling?.scheduledDate
        ? new Date(job.scheduling.scheduledDate).toDateString()
        : null;
      if (date) {
        if (!acc[date]) acc[date] = [];
        acc[date].push(job);
      }
      return acc;
    },
    {} as Record<string, Job[]>
  );

  const navigatePrev = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const navigateNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const navigateToday = () => {
    setCurrentDate(new Date());
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const handleAddJob = (date: Date) => {
    setSelectedDateForNewJob(date);
    setIsAddJobModalOpen(true);
  };

  const handleJobCreated = () => {
    refetch();
    setIsAddJobModalOpen(false);
    setSelectedDateForNewJob(undefined);
  };

  return (
    <div className="p-6 h-full flex flex-col">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Schedule</h1>
        <p className="text-gray-600">Manage job scheduling and appointments</p>
      </header>

      {/* Controls */}
      <div className="card p-4 mb-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Navigation */}
          <div className="flex items-center gap-2">
            <button onClick={navigatePrev} className="btn-secondary p-2">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={navigateToday} className="btn-secondary px-3 py-2 text-sm">
              Today
            </button>
            <button onClick={navigateNext} className="btn-secondary p-2">
              <ChevronRight className="w-5 h-5" />
            </button>
            <span className="ml-2 font-medium text-gray-900">
              {viewMode === 'week'
                ? `${dateRange[0]?.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })} - ${dateRange[6]?.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}`
                : currentDate.toLocaleDateString('en-CA', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
            </span>
          </div>

          {/* View mode & filters */}
          <div className="flex items-center gap-4">
            <select
              value={selectedTechnician}
              onChange={(e) => setSelectedTechnician(e.target.value)}
              className="input w-48"
            >
              <option value="all">All Technicians</option>
              {technicians
                .filter((t) => t.employmentStatus === 'ACTIVE')
                .map((tech) => (
                  <option key={tech.id} value={tech.id}>
                    {tech.fullName}
                  </option>
                ))}
            </select>

            <div className="flex rounded-lg overflow-hidden border border-gray-200">
              <button
                onClick={() => setViewMode('week')}
                className={clsx(
                  'px-3 py-2 text-sm font-medium',
                  viewMode === 'week' ? 'bg-brand-600 text-white' : 'bg-white text-gray-700'
                )}
              >
                Week
              </button>
              <button
                onClick={() => setViewMode('day')}
                className={clsx(
                  'px-3 py-2 text-sm font-medium',
                  viewMode === 'day' ? 'bg-brand-600 text-white' : 'bg-white text-gray-700'
                )}
              >
                Day
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading schedule...</div>
      ) : (
        <div className="flex-1 card overflow-hidden">
          <div
            className={clsx(
              'grid h-full',
              viewMode === 'week' ? 'grid-cols-7' : 'grid-cols-1'
            )}
          >
            {dateRange.map((date) => {
              const dateKey = date.toDateString();
              const dayJobs = jobsByDate[dateKey] || [];
              const today = isToday(date);

              return (
                <div
                  key={dateKey}
                  className={clsx(
                    'border-r last:border-r-0 flex flex-col',
                    today && 'bg-brand-50/30'
                  )}
                >
                  {/* Date Header */}
                  <div
                    className={clsx(
                      'p-3 border-b text-center',
                      today ? 'bg-brand-100' : 'bg-gray-50'
                    )}
                  >
                    <div className="text-xs text-gray-500 uppercase">
                      {date.toLocaleDateString('en-CA', { weekday: 'short' })}
                    </div>
                    <div
                      className={clsx(
                        'text-lg font-semibold',
                        today ? 'text-brand-700' : 'text-gray-900'
                      )}
                    >
                      {date.getDate()}
                    </div>
                  </div>

                  {/* Jobs */}
                  <div className="flex-1 overflow-y-auto p-2 space-y-2">
                    {dayJobs.length === 0 ? (
                      <div className="text-center py-4 text-xs text-gray-400">No jobs</div>
                    ) : (
                      dayJobs.map((job) => (
                        <div
                          key={job.id}
                          className="p-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-gray-900">
                              {job.seNumber || job.sytelineOrderNumber}
                            </span>
                            {job.scheduling?.startTime && (
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(job.scheduling.startTime).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-600 truncate">{job.customerName}</p>
                          <p className="text-xs text-gray-500 truncate">{job.city}</p>
                          {job.technician && (
                            <div className="mt-1 flex items-center gap-1 text-xs text-brand-600">
                              <User className="w-3 h-3" />
                              {job.technician.fullName}
                            </div>
                          )}
                        </div>
                      ))
                    )}

                    {/* Add job button */}
                    <button
                      onClick={() => handleAddJob(date)}
                      className="w-full p-2 border border-dashed border-gray-300 rounded-lg text-gray-400 hover:border-brand-400 hover:text-brand-600 transition-colors flex items-center justify-center gap-1 text-xs"
                    >
                      <Plus className="w-3 h-3" />
                      Add Job
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add Job Modal */}
      <ServiceOrdersModal
        isOpen={isAddJobModalOpen}
        onClose={() => {
          setIsAddJobModalOpen(false);
          setSelectedDateForNewJob(undefined);
        }}
        onJobCreated={handleJobCreated}
        selectedDate={selectedDateForNewJob}
      />
    </div>
  );
}
